import { Collapse, InputNumber, Space } from "antd"
import RadioOption from "../../Proposals/PaymentOptions/RadioOption"
import VSpace from "../../common/VSpace"
import SettingsCard from "../SettingsCard"
import { toPaymentScheduleRows, updateIntervalBasedRecurringPayment, updateIntervalBasedTotalPayments, updateQuotedAndTimeAndMaterialsSelectedOption } from "../../Proposals/paymentScheduleModel"
import { useReducer, useState } from "react"
import InvoiceRateSelector from "../../Proposals/PaymentOptions/InvoiceRateSelector"
import { PaymentScheduleOptions } from "../../../domain/proposals/settings/proposalSettings"
import { useUpdatePaymentScheduleOptionsMutation } from "../../../domain/proposals/settings/proposalSettingsQueries"
import { isEqual } from "lodash"
import { progressBasedReducer } from "./progressBasedReducer"
import PaymentSchedule from "../../Proposals/PaymentSchedule"

type Props = {
  serverOptions: PaymentScheduleOptions
}

const QuotedAndTimeAndMaterialsSettings = ({serverOptions}: Props) => {
  const [paymentScheduleOptions, setPaymentScheduleOptions] = useState<PaymentScheduleOptions>(serverOptions)
  const [progressBased, dispatch] = useReducer(progressBasedReducer, serverOptions.quotedAndTimeAndMaterials.progressBased)
  const updateProposalSettingsMutation = useUpdatePaymentScheduleOptionsMutation()

  const dataToSave = {
    ...serverOptions,
    quotedAndTimeAndMaterials: {
      ...paymentScheduleOptions.quotedAndTimeAndMaterials,
      progressBased
    }
  }
  const standardizeForComparison = (options: PaymentScheduleOptions) => {
    return {
      ...options,
      quotedAndTimeAndMaterials: {
        ...options.quotedAndTimeAndMaterials,
        progressBased: toPaymentScheduleRows(options.quotedAndTimeAndMaterials.progressBased)
      }
    }
  }
  const hasChanges = !isEqual(standardizeForComparison(dataToSave), standardizeForComparison(serverOptions))

  return <SettingsCard
    title="Invoicing Schedule for Quoted and Time & Materials"
    description="Set the default invoicing schedule for work areas priced as Quoted or Time & Material. This schedule will appear on proposals automatically."
    onUpdate={() => updateProposalSettingsMutation.mutate(dataToSave)}
    isUpdating={updateProposalSettingsMutation.isLoading}
    updateDisabled={!hasChanges}
  >
    <VSpace>
      Default Payment Schedule
      <RadioOption
        label="Progress-based"
        description="Pay by completion milestone ie: 20% upfront, 80% on completion"
        checked={paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'progress-based'}
        onClick={() => setPaymentScheduleOptions(updateQuotedAndTimeAndMaterialsSelectedOption(paymentScheduleOptions, 'progress-based'))}
      />
      <RadioOption
        label="Interval-based"
        description="Equally distribute total across your chosen set of payments"
        checked={paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'interval-based'}
        onClick={() => setPaymentScheduleOptions(updateQuotedAndTimeAndMaterialsSelectedOption(paymentScheduleOptions, 'interval-based'))}
      />

      <Collapse
        items={[
          {
            key: 'progress-based',
            label: 'Default Progress-Based Settings',
            children: <div style={{maxWidth: '900px'}}>
              <PaymentSchedule
                paymentSchedule={progressBased}
                total={0}
                dispatch={dispatch}
                hideAmounts
              />
            </div>
          }
        ]}
      />

      <Collapse
        items={[
          {
            key: 'interval-based',
            label: 'Default Interval-Based Settings',
            children: <VSpace>
              <Space>
                Total number of payments
                <InputNumber
                  value={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.totalPayments}
                  min={1}
                  max={100}
                  onChange={value => setPaymentScheduleOptions(updateIntervalBasedTotalPayments(paymentScheduleOptions, value ?? 1))}
                />
              </Space>
              <InvoiceRateSelector
                recurringPayment={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.recurring}
                totalPayments={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.totalPayments}
                onChange={value => setPaymentScheduleOptions(updateIntervalBasedRecurringPayment(paymentScheduleOptions, value))}
                showEstimated={false}
                hideEstimatedStartDate
              />
            </VSpace>
          }
        ]}
      />
    </VSpace>
  </SettingsCard>
}

export default QuotedAndTimeAndMaterialsSettings